import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Search, ChevronDown, ChevronRight, AlertTriangle, Filter, X,
  Pill, Edit2, RotateCcw, Plus, Trash2, Lock, Save,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/axios';
import { useAuthStore, ROL } from '@/store/auth.store';
import { getErrorMessage } from '@/lib/errors';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Presentacion {
  id_presentacion: string;
  forma_farmaceutica: string;
  via_administracion: string;
  concentracion_valor: number | null;
  concentracion_unidad: string;
  empaque_tipo: string;
  unidades_por_empaque: number | null;
  es_pediatrico: boolean;
}

interface Interaccion {
  principio_activo: string;
  severidad: 'CRITICA' | 'ALTA' | 'MODERADA' | 'LEVE';
}

interface Posologia {
  adulto_diaria_max_mg: number | null;
  intervalo_min_horas: number | null;
  intervalo_rec_horas: number | null;
  peds_requiere_peso: boolean;
  peds_dosis_mg_kg: number | null;
  peds_diaria_max_mg_kg: number | null;
}

interface Medicamento {
  id: string;
  nombre_generico: string;
  grupo_fuente: string;
  nombres_comerciales: string[];
  requiere_receta: boolean;
  presentaciones: Presentacion[];
  posologia_general: Posologia | null;
  interacciones_clave: Interaccion[];
  tieneOverride: boolean;
  origenClinicaId?: string | null;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const FORMAS = ['TABLETA', 'COMPRIMIDO', 'ENJUAGUE', 'CAPSULA', 'SOLUCION', 'SUSPENSION', 'INYECTABLE', 'CREMA', 'GEL', 'JARABE', 'GOTAS', 'OVULOS', 'SUPOSITORIO', 'POLVO', 'SPRAY', 'PARCHE', 'OTRO'];
const VIAS_ADM = ['ORAL', 'INTRAVENOSA', 'INTRAMUSCULAR', 'SUBCUTANEA', 'TOPICA', 'INHALADA', 'RECTAL', 'OFTALMICA', 'OTICA', 'SUBLINGUAL', 'TRANSDERMAL', 'NASAL'];
const UNIDADES = ['mg', 'mL', 'mcg', 'UI', 'g', '%', 'mg/mL', 'mcg/mL', 'mg/g'];
const EMPAQUES = ['CAJA', 'FRASCO', 'AMPOLLETA', 'VIAL', 'BLISTER', 'TUBO', 'SOBRE', 'GOTERO'];

const SEVERIDAD_COLOR: Record<string, string> = {
  CRITICA:  'text-red-700 bg-red-50 border-red-200',
  ALTA:     'text-orange-700 bg-orange-50 border-orange-200',
  MODERADA: 'text-amber-700 bg-amber-50 border-amber-200',
  LEVE:     'text-blue-700 bg-blue-50 border-blue-200',
};
const SEVERIDAD_BADGE: Record<string, string> = {
  CRITICA:  'bg-red-100 text-red-700',
  ALTA:     'bg-orange-100 text-orange-700',
  MODERADA: 'bg-amber-100 text-amber-700',
  LEVE:     'bg-blue-100 text-blue-700',
};

const emptyPosologia: Posologia = {
  adulto_diaria_max_mg: null, intervalo_min_horas: null, intervalo_rec_horas: null,
  peds_requiere_peso: false, peds_dosis_mg_kg: null, peds_diaria_max_mg_kg: null,
};

function genPresId() {
  return `PRES-CLI-${Date.now()}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`;
}

const emptyPres = (): Presentacion => ({
  id_presentacion: genPresId(),
  forma_farmaceutica: 'TABLETA',
  via_administracion: 'ORAL',
  concentracion_valor: null,
  concentracion_unidad: 'mg',
  empaque_tipo: 'CAJA',
  unidades_por_empaque: null,
  es_pediatrico: false,
});

const inputCls = 'w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white';
const selCls = `${inputCls} pr-8`;

// ── Shared editors (reused by EditarMedModal y CrearMedModal) ──────────────────

function NombresComercialesEditor({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const [nuevoNc, setNuevoNc] = useState('');

  function addNc() {
    const v = nuevoNc.trim();
    if (!v || value.includes(v)) return;
    onChange([...value, v]);
    setNuevoNc('');
  }
  function removeNc(idx: number) {
    onChange(value.filter((_, i) => i !== idx));
  }

  return (
    <section>
      <p className="text-[11px] font-semibold text-text-secondary uppercase tracking-widest mb-0.5">Nombres comerciales</p>
      <p className="text-[11px] text-text-secondary mb-2">Se comparten con todas las clínicas al guardar.</p>
      <div className="flex flex-wrap gap-1.5 mb-2 min-h-[28px]">
        {value.length === 0 && (
          <span className="text-xs text-text-secondary italic">Sin nombres comerciales</span>
        )}
        {value.map((nc, i) => (
          <span key={i} className="flex items-center gap-1 bg-gray-100 text-text-primary text-xs px-2.5 py-1 rounded-full">
            {nc}
            <button type="button" onClick={() => removeNc(i)}
              className="text-text-secondary hover:text-red-500 transition-colors ml-0.5">
              <X size={11} />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          value={nuevoNc}
          onChange={e => setNuevoNc(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addNc())}
          placeholder="Ej. Glucophage, Metformax..."
          className={`${inputCls} flex-1`}
        />
        <button type="button" onClick={addNc}
          className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-text-secondary rounded-lg text-sm transition-colors">
          <Plus size={13} /> Agregar
        </button>
      </div>
    </section>
  );
}

function PresentacionesEditor({ value, onChange }: { value: Presentacion[]; onChange: (v: Presentacion[]) => void }) {
  function setPres(idx: number, field: keyof Presentacion, val: any) {
    const ps = [...value];
    ps[idx] = { ...ps[idx], [field]: val };
    onChange(ps);
  }
  function addPres() {
    onChange([...value, emptyPres()]);
  }
  function removePres(idx: number) {
    onChange(value.filter((_, i) => i !== idx));
  }

  return (
    <section>
      <div className="flex items-center justify-between mb-2">
        <p className="text-[11px] font-semibold text-text-secondary uppercase tracking-widest">
          Presentaciones ({value.length})
        </p>
        <button type="button" onClick={addPres}
          className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 font-medium transition-colors">
          <Plus size={12} /> Agregar
        </button>
      </div>

      {value.length === 0 ? (
        <p className="text-xs text-text-secondary italic py-2">Sin presentaciones. Agrega una con el botón de arriba.</p>
      ) : (
        <div className="space-y-2">
          {value.map((p, i) => (
            <div key={p.id_presentacion} className="border border-gray-100 rounded-xl p-3 bg-gray-50/50 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-medium text-text-secondary">Presentación {i + 1}</span>
                <button type="button" onClick={() => removePres(i)}
                  className="p-1 rounded text-text-secondary hover:text-red-500 hover:bg-red-50 transition-colors">
                  <Trash2 size={13} />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] text-text-secondary mb-0.5 block">Forma farmacéutica</label>
                  <select value={p.forma_farmaceutica} onChange={e => setPres(i, 'forma_farmaceutica', e.target.value)} className={selCls}>
                    {FORMAS.map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] text-text-secondary mb-0.5 block">Vía de administración</label>
                  <select value={p.via_administracion} onChange={e => setPres(i, 'via_administracion', e.target.value)} className={selCls}>
                    {VIAS_ADM.map(v => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] text-text-secondary mb-0.5 block">Concentración</label>
                  <div className="flex gap-1">
                    <input type="number" min="0"
                      value={p.concentracion_valor ?? ''}
                      onChange={e => setPres(i, 'concentracion_valor', e.target.value === '' ? null : Number(e.target.value))}
                      placeholder="Valor"
                      className="border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white w-20 flex-shrink-0" />
                    <select value={p.concentracion_unidad} onChange={e => setPres(i, 'concentracion_unidad', e.target.value)}
                      className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white flex-1">
                      {UNIDADES.map(u => <option key={u} value={u}>{u}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-[10px] text-text-secondary mb-0.5 block">Empaque</label>
                  <div className="flex gap-1">
                    <select value={p.empaque_tipo} onChange={e => setPres(i, 'empaque_tipo', e.target.value)}
                      className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white flex-1">
                      {EMPAQUES.map(e => <option key={e} value={e}>{e}</option>)}
                    </select>
                    <input type="number" min="1"
                      value={p.unidades_por_empaque ?? ''}
                      onChange={e => setPres(i, 'unidades_por_empaque', e.target.value === '' ? null : Number(e.target.value))}
                      placeholder="Uds."
                      className="border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white w-16 flex-shrink-0" />
                  </div>
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input type="checkbox" checked={p.es_pediatrico}
                  onChange={e => setPres(i, 'es_pediatrico', e.target.checked)}
                  className="w-3.5 h-3.5 rounded accent-primary-500" />
                <span className="text-xs text-text-secondary">Presentación pediátrica</span>
              </label>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function PosologiaEditor({ value, onChange }: { value: Posologia; onChange: (v: Posologia) => void }) {
  function setPoso(field: keyof Posologia, val: string) {
    if (field === 'peds_requiere_peso') {
      onChange({ ...value, peds_requiere_peso: val === 'true' });
    } else {
      onChange({ ...value, [field]: val === '' ? null : Number(val) });
    }
  }

  return (
    <section>
      <p className="text-[11px] font-semibold text-text-secondary uppercase tracking-widest mb-2">Posología general</p>
      <div className="grid grid-cols-2 gap-3 bg-blue-50/50 border border-blue-100 rounded-xl p-3">
        {([
          ['intervalo_rec_horas',    'Frecuencia recomendada (h)'],
          ['intervalo_min_horas',    'Intervalo mínimo (h)'],
          ['adulto_diaria_max_mg',   'Dosis adulto máx/día (mg)'],
          ['peds_dosis_mg_kg',       'Peds dosis (mg/kg)'],
          ['peds_diaria_max_mg_kg',  'Peds máx/día (mg/kg)'],
        ] as [keyof Posologia, string][]).map(([field, label]) => (
          <label key={field} className="flex flex-col gap-0.5">
            <span className="text-[11px] text-text-secondary">{label}</span>
            <input
              type="number"
              min="0"
              step="any"
              value={(value[field] as number | null) ?? ''}
              onChange={e => setPoso(field, e.target.value)}
              placeholder="—"
              className="border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white"
            />
          </label>
        ))}
      </div>
    </section>
  );
}

// ── EditarMedModal ─────────────────────────────────────────────────────────────

interface EditForm {
  nombresComerciales: string[];
  presentaciones: Presentacion[];
  posologia: Posologia;
}

function EditarMedModal({ med, onClose }: { med: Medicamento; onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState<EditForm>({
    nombresComerciales: [...med.nombres_comerciales],
    presentaciones: med.presentaciones.map(p => ({ ...p })),
    posologia: med.posologia_general ? { ...med.posologia_general } : { ...emptyPosologia },
  });
  const [interaccionesOpen, setInteraccionesOpen] = useState(false);

  const saveMut = useMutation({
    mutationFn: () => Promise.all([
      api.put(`/api/vademecum-clinico/${med.id}/nombres-comerciales`, {
        nombresComerciales: form.nombresComerciales,
      }),
      api.put(`/api/vademecum-clinico/${med.id}`, {
        presentacionesOverride: form.presentaciones,
        posologiaOverride: form.posologia,
      }),
    ]),
    onSuccess: () => {
      toast.success('Medicamento actualizado');
      qc.invalidateQueries({ queryKey: ['vademecum-clinico'] });
      qc.invalidateQueries({ queryKey: ['vademecum-general'] });
      onClose();
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al guardar')),
  });

  const resetMut = useMutation({
    mutationFn: () => api.delete(`/api/vademecum-clinico/${med.id}/override`).then(r => r.data),
    onSuccess: () => {
      toast.success('Restablecido a valores del Vademecum General');
      qc.invalidateQueries({ queryKey: ['vademecum-clinico'] });
      onClose();
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al restablecer')),
  });

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col">

        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div>
            <p className="text-[10px] font-semibold text-text-secondary uppercase tracking-widest mb-0.5">Vademecum Clínico</p>
            <h2 className="font-bold text-text-primary text-lg leading-tight">{med.nombre_generico}</h2>
            <p className="text-xs text-text-secondary mt-0.5">{med.grupo_fuente}</p>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-text-secondary mt-1">
            <X size={18} />
          </button>
        </div>

        {/* Body (scrollable) */}
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-6">

          <NombresComercialesEditor
            value={form.nombresComerciales}
            onChange={nombresComerciales => setForm(f => ({ ...f, nombresComerciales }))}
          />

          <PresentacionesEditor
            value={form.presentaciones}
            onChange={presentaciones => setForm(f => ({ ...f, presentaciones }))}
          />

          <PosologiaEditor
            value={form.posologia}
            onChange={posologia => setForm(f => ({ ...f, posologia }))}
          />

          {/* ── Interacciones (solo lectura) ─────────────────────────────────── */}
          {med.interacciones_clave.length > 0 && (
            <section>
              <button
                type="button"
                onClick={() => setInteraccionesOpen(p => !p)}
                className="flex items-center gap-2 w-full text-left"
              >
                <Lock size={12} className="text-text-secondary" />
                <span className="text-[11px] font-semibold text-text-secondary uppercase tracking-widest">
                  Interacciones clave ({med.interacciones_clave.length}) — no modificables
                </span>
                {interaccionesOpen ? <ChevronDown size={13} className="ml-auto text-text-secondary" /> : <ChevronRight size={13} className="ml-auto text-text-secondary" />}
              </button>
              {interaccionesOpen && (
                <div className="mt-2 space-y-1">
                  {med.interacciones_clave.map((ix, idx) => (
                    <div key={idx} className={cn('flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-xs font-medium', SEVERIDAD_COLOR[ix.severidad])}>
                      <AlertTriangle size={11} className="shrink-0" />
                      <span className="flex-1">{ix.principio_activo}</span>
                      <span className={cn('text-[10px] px-1.5 py-0.5 rounded-full font-semibold', SEVERIDAD_BADGE[ix.severidad])}>
                        {ix.severidad}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center gap-2 flex-shrink-0">
          {med.tieneOverride && (
            <button type="button" onClick={() => resetMut.mutate()} disabled={resetMut.isPending}
              className="flex items-center gap-1.5 text-xs text-text-secondary hover:text-text-primary border border-gray-200 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 mr-auto">
              <RotateCcw size={12} /> Restablecer
            </button>
          )}
          <div className={cn('flex gap-2', !med.tieneOverride && 'ml-auto')}>
            <button type="button" onClick={onClose}
              className="px-4 py-2 border border-gray-200 text-text-secondary rounded-lg text-sm hover:bg-gray-50 transition-colors">
              Cancelar
            </button>
            <button type="button" onClick={() => saveMut.mutate()} disabled={saveMut.isPending}
              className="flex items-center gap-1.5 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors disabled:opacity-60">
              <Save size={14} /> {saveMut.isPending ? 'Guardando...' : 'Guardar cambios'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── CrearMedModal ────────────────────────────────────────────────────────────

interface CrearForm {
  nombreGenerico: string;
  grupoFuente: string;
  requiereReceta: boolean;
  nombresComerciales: string[];
  presentaciones: Presentacion[];
  posologia: Posologia;
}

const emptyCrearForm: CrearForm = {
  nombreGenerico: '',
  grupoFuente: '',
  requiereReceta: false,
  nombresComerciales: [],
  presentaciones: [],
  posologia: { ...emptyPosologia },
};

function CrearMedModal({ grupos, onClose }: { grupos: string[]; onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState<CrearForm>({ ...emptyCrearForm });

  const createMut = useMutation({
    mutationFn: () => api.post('/api/vademecum-clinico', {
      nombreGenerico: form.nombreGenerico.trim(),
      grupoFuente: form.grupoFuente.trim(),
      requiereReceta: form.requiereReceta,
      nombresComerciales: form.nombresComerciales,
      presentaciones: form.presentaciones,
      posologiaGeneral: form.posologia,
    }).then(r => r.data),
    onSuccess: () => {
      toast.success('Medicamento agregado al Vademecum General — ya está disponible para todas las clínicas');
      qc.invalidateQueries({ queryKey: ['vademecum-clinico'] });
      qc.invalidateQueries({ queryKey: ['vademecum-general'] });
      qc.invalidateQueries({ queryKey: ['vademecum-grupos'] });
      onClose();
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al crear el medicamento')),
  });

  const puedeGuardar = form.nombreGenerico.trim().length > 0 && form.grupoFuente.trim().length > 0;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col">

        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div>
            <p className="text-[10px] font-semibold text-text-secondary uppercase tracking-widest mb-0.5">Vademecum Clínico</p>
            <h2 className="font-bold text-text-primary text-lg leading-tight">Agregar medicamento</h2>
            <p className="text-xs text-text-secondary mt-0.5">Se guarda en el Vademecum General — todas las clínicas lo verán.</p>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-text-secondary mt-1">
            <X size={18} />
          </button>
        </div>

        {/* Body (scrollable) */}
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-6">

          <section className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-[11px] font-semibold text-text-secondary uppercase tracking-widest">Nombre genérico *</span>
              <input
                value={form.nombreGenerico}
                onChange={e => setForm(f => ({ ...f, nombreGenerico: e.target.value }))}
                placeholder="Ej. Paracetamol"
                className={inputCls}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[11px] font-semibold text-text-secondary uppercase tracking-widest">Grupo *</span>
              <input
                value={form.grupoFuente}
                onChange={e => setForm(f => ({ ...f, grupoFuente: e.target.value }))}
                placeholder="Ej. Analgesia"
                list="vademecum-grupos-existentes"
                className={inputCls}
              />
              <datalist id="vademecum-grupos-existentes">
                {grupos.map(g => <option key={g} value={g} />)}
              </datalist>
            </label>
          </section>

          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input type="checkbox" checked={form.requiereReceta}
              onChange={e => setForm(f => ({ ...f, requiereReceta: e.target.checked }))}
              className="w-3.5 h-3.5 rounded accent-primary-500" />
            <span className="text-sm text-text-secondary">Requiere receta</span>
          </label>

          <NombresComercialesEditor
            value={form.nombresComerciales}
            onChange={nombresComerciales => setForm(f => ({ ...f, nombresComerciales }))}
          />

          <PresentacionesEditor
            value={form.presentaciones}
            onChange={presentaciones => setForm(f => ({ ...f, presentaciones }))}
          />

          <PosologiaEditor
            value={form.posologia}
            onChange={posologia => setForm(f => ({ ...f, posologia }))}
          />
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center gap-2 flex-shrink-0">
          <div className="flex gap-2 ml-auto">
            <button type="button" onClick={onClose}
              className="px-4 py-2 border border-gray-200 text-text-secondary rounded-lg text-sm hover:bg-gray-50 transition-colors">
              Cancelar
            </button>
            <button type="button" onClick={() => createMut.mutate()} disabled={!puedeGuardar || createMut.isPending}
              className="flex items-center gap-1.5 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors disabled:opacity-60">
              <Save size={14} /> {createMut.isPending ? 'Guardando...' : 'Agregar medicamento'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── MedicamentoCard ───────────────────────────────────────────────────────────

function MedicamentoCard({ med, canEdit, onEdit }: { med: Medicamento; canEdit: boolean; onEdit: () => void }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={cn('border rounded-xl overflow-hidden transition-all',
      med.tieneOverride ? 'border-purple-200' : expanded ? 'border-primary-200' : 'border-gray-100'
    )}>
      <button type="button" onClick={() => setExpanded(p => !p)}
        className={cn('flex items-center justify-between w-full px-4 py-3 text-left transition-colors',
          med.tieneOverride ? 'bg-purple-50 hover:bg-purple-50/80' : expanded ? 'bg-primary-50' : 'bg-white hover:bg-gray-50'
        )}>
        <div className="flex items-center gap-3 min-w-0">
          <Pill size={14} className={med.tieneOverride ? 'text-purple-400' : 'text-primary-400'} />
          <div className="min-w-0">
            <span className="font-medium text-sm text-text-primary">{med.nombre_generico}</span>
            {med.nombres_comerciales.length > 0 && (
              <span className="text-[11px] text-text-secondary ml-2">
                ({med.nombres_comerciales.slice(0, 3).join(', ')}{med.nombres_comerciales.length > 3 ? ` +${med.nombres_comerciales.length - 3}` : ''})
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 ml-2">
          {med.tieneOverride && (
            <span className="text-[10px] bg-purple-100 text-purple-700 border border-purple-200 px-2 py-0.5 rounded-full font-medium">Modificado</span>
          )}
          {med.origenClinicaId && (
            <span className="text-[10px] bg-teal-50 text-teal-700 border border-teal-200 px-2 py-0.5 rounded-full font-medium">Agregado por una clínica</span>
          )}
          {med.requiere_receta && (
            <span className="text-[10px] bg-amber-50 text-amber-600 border border-amber-200 px-2 py-0.5 rounded-full">Receta</span>
          )}
          {expanded ? <ChevronDown size={14} className="text-text-secondary" /> : <ChevronRight size={14} className="text-text-secondary" />}
        </div>
      </button>

      {expanded && (
        <div className="px-4 py-3 bg-white border-t border-gray-100 space-y-3">
          {/* Nombres comerciales */}
          <div>
            <p className="text-[11px] font-semibold text-text-secondary uppercase tracking-wide mb-1.5">Nombres comerciales</p>
            {med.nombres_comerciales.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {med.nombres_comerciales.map(nc => (
                  <span key={nc} className="text-xs bg-gray-100 text-text-secondary px-2.5 py-0.5 rounded-full">{nc}</span>
                ))}
              </div>
            ) : (
              <p className="text-xs text-text-secondary italic">Sin nombres comerciales</p>
            )}
          </div>

          {/* Presentaciones */}
          <div>
            <p className="text-[11px] font-semibold text-text-secondary uppercase tracking-wide mb-1.5">
              Presentaciones ({med.presentaciones.length})
            </p>
            {med.presentaciones.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-gray-50 text-text-secondary">
                      <th className="text-left px-2 py-1.5 rounded-l font-medium">Forma</th>
                      <th className="text-left px-2 py-1.5 font-medium">Concentración</th>
                      <th className="text-left px-2 py-1.5 font-medium">Vía</th>
                      <th className="text-left px-2 py-1.5 font-medium">Empaque</th>
                      <th className="text-left px-2 py-1.5 rounded-r font-medium">Peds</th>
                    </tr>
                  </thead>
                  <tbody>
                    {med.presentaciones.map(p => (
                      <tr key={p.id_presentacion} className="border-b border-gray-50">
                        <td className="px-2 py-1.5 font-medium capitalize">{p.forma_farmaceutica.charAt(0) + p.forma_farmaceutica.slice(1).toLowerCase()}</td>
                        <td className="px-2 py-1.5">{p.concentracion_valor} {p.concentracion_unidad}</td>
                        <td className="px-2 py-1.5 capitalize">{p.via_administracion.toLowerCase()}</td>
                        <td className="px-2 py-1.5">{p.empaque_tipo} × {p.unidades_por_empaque}</td>
                        <td className="px-2 py-1.5">
                          {p.es_pediatrico
                            ? <span className="text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded-full">Sí</span>
                            : <span className="text-[10px] text-text-secondary">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-xs text-text-secondary italic">Sin presentaciones</p>
            )}
          </div>

          {/* Posología */}
          {med.posologia_general && (
            <div>
              <p className="text-[11px] font-semibold text-text-secondary uppercase tracking-wide mb-1.5">Posología general</p>
              <div className={cn('rounded-lg p-3 text-xs', med.tieneOverride ? 'bg-purple-50 border border-purple-100' : 'bg-blue-50 border border-blue-100')}>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-text-primary">
                  {med.posologia_general.intervalo_rec_horas != null && (
                    <span>Frecuencia: cada <strong>{med.posologia_general.intervalo_rec_horas} h</strong></span>
                  )}
                  {med.posologia_general.intervalo_min_horas != null && (
                    <span>Mín: {med.posologia_general.intervalo_min_horas} h</span>
                  )}
                  {med.posologia_general.adulto_diaria_max_mg != null && (
                    <span>Adulto máx/día: <strong>{med.posologia_general.adulto_diaria_max_mg} mg</strong></span>
                  )}
                  {med.posologia_general.peds_dosis_mg_kg != null && (
                    <span>Peds: {med.posologia_general.peds_dosis_mg_kg} mg/kg</span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Interacciones */}
          {med.interacciones_clave.length > 0 && (
            <div>
              <p className="text-[11px] font-semibold text-text-secondary uppercase tracking-wide mb-1.5">
                Interacciones clave ({med.interacciones_clave.length})
              </p>
              <div className="space-y-1">
                {med.interacciones_clave.map((ix, idx) => (
                  <div key={idx} className={cn('flex items-center gap-2 px-2 py-1.5 rounded border text-xs font-medium', SEVERIDAD_COLOR[ix.severidad])}>
                    <AlertTriangle size={11} className="shrink-0" />
                    <span>{ix.principio_activo}</span>
                    <span className={cn('ml-auto text-[10px] px-1.5 py-0.5 rounded-full font-semibold', SEVERIDAD_BADGE[ix.severidad])}>
                      {ix.severidad}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Acciones */}
          {canEdit && (
            <div className="pt-1">
              <button type="button" onClick={onEdit}
                className="flex items-center gap-1.5 text-xs text-primary-600 hover:text-primary-700 border border-primary-200 bg-primary-50 hover:bg-primary-100 px-3 py-1.5 rounded-lg transition-colors font-medium">
                <Edit2 size={12} /> Editar medicamento
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function VademecumClinicoPage() {
  const { usuario } = useAuthStore();
  const canEdit = usuario?.rolId === ROL.SUPER_ADMIN
    || usuario?.rolId === ROL.ADMIN_CLINICA
    || usuario?.rolId === ROL.MEDICO_ADMIN;

  const [q, setQ] = useState('');
  const [grupoFiltro, setGrupoFiltro] = useState('');
  const [recetaFiltro, setRecetaFiltro] = useState<'' | 'true' | 'false'>('');
  const [pediatricoFiltro, setPediatricoFiltro] = useState<'' | 'true' | 'false'>('');
  const [expandedGrupos, setExpandedGrupos] = useState<Set<string>>(new Set());
  const [editingMed, setEditingMed] = useState<Medicamento | null>(null);
  const [creando, setCreando] = useState(false);

  const { data: grupos = [] } = useQuery<string[]>({
    queryKey: ['vademecum-grupos'],
    queryFn: () => api.get('/api/vademecum/grupos').then(r => r.data),
    staleTime: Infinity,
  });

  const { data: medicamentos = [], isFetching } = useQuery<Medicamento[]>({
    queryKey: ['vademecum-clinico', q, grupoFiltro, recetaFiltro, pediatricoFiltro],
    queryFn: () => api.get('/api/vademecum-clinico', {
      params: {
        ...(q ? { q } : {}),
        ...(grupoFiltro ? { grupo: grupoFiltro } : {}),
        ...(recetaFiltro !== '' ? { requiereReceta: recetaFiltro } : {}),
        ...(pediatricoFiltro !== '' ? { pediatrico: pediatricoFiltro } : {}),
      },
    }).then(r => r.data),
    staleTime: 60_000,
  });

  const byGrupo = medicamentos.reduce<Record<string, Medicamento[]>>((acc, m) => {
    (acc[m.grupo_fuente] ??= []).push(m);
    return acc;
  }, {});

  const modificados = medicamentos.filter(m => m.tieneOverride).length;
  const hayFiltros = q || grupoFiltro || recetaFiltro || pediatricoFiltro;

  function toggleGrupo(g: string) {
    setExpandedGrupos(prev => {
      const next = new Set(prev);
      if (next.has(g)) next.delete(g); else next.add(g);
      return next;
    });
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-text-primary">Vademecum Clínico</h1>
          <p className="text-sm text-text-secondary mt-1">
            {medicamentos.length.toLocaleString()} medicamentos
            {modificados > 0 && <span className="ml-2 text-purple-600 font-medium">· {modificados} con especificaciones clínicas</span>}
          </p>
        </div>
        {canEdit && (
          <button type="button" onClick={() => setCreando(true)}
            className="flex items-center gap-1.5 px-3 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors shrink-0">
            <Plus size={14} /> Agregar medicamento
          </button>
        )}
      </div>

      {canEdit && (
        <div className="bg-purple-50 border border-purple-100 rounded-xl px-4 py-3 text-sm text-purple-800">
          <strong>Modo edición activo.</strong> Los nombres comerciales y los medicamentos que agregues se comparten con
          todas las clínicas (Vademecum General). Las presentaciones y la posología que edites aplican solo en esta clínica.
          El nombre genérico e interacciones de medicamentos existentes no son modificables.
        </div>
      )}

      {/* Filtros */}
      <div className="bg-white border border-gray-100 rounded-xl p-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary pointer-events-none" />
            <input value={q} onChange={e => setQ(e.target.value)}
              placeholder="Buscar por nombre genérico o comercial..."
              className="w-full pl-8 pr-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
          </div>
          <select value={grupoFiltro} onChange={e => setGrupoFiltro(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white">
            <option value="">Todos los grupos</option>
            {grupos.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
          <select value={recetaFiltro} onChange={e => setRecetaFiltro(e.target.value as '' | 'true' | 'false')}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white">
            <option value="">Todos (receta)</option>
            <option value="true">Requiere receta</option>
            <option value="false">Sin receta</option>
          </select>
          <select value={pediatricoFiltro} onChange={e => setPediatricoFiltro(e.target.value as '' | 'true' | 'false')}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white">
            <option value="">Todos</option>
            <option value="true">Solo pediátrico</option>
            <option value="false">No pediátrico</option>
          </select>
          {hayFiltros && (
            <button type="button" onClick={() => { setQ(''); setGrupoFiltro(''); setRecetaFiltro(''); setPediatricoFiltro(''); }}
              className="flex items-center gap-1 text-xs text-text-secondary hover:text-text-primary px-2 py-1.5 rounded-lg hover:bg-gray-100 transition-colors">
              <X size={12} /> Limpiar
            </button>
          )}
        </div>
        {isFetching && <p className="text-[11px] text-text-secondary mt-2">Cargando...</p>}
      </div>

      {/* Resultados */}
      {Object.keys(byGrupo).length === 0 && !isFetching && (
        <div className="text-center py-12 text-text-secondary">
          <Filter size={32} className="mx-auto mb-3 opacity-40" />
          <p className="text-sm">No se encontraron medicamentos</p>
        </div>
      )}

      {Object.entries(byGrupo).map(([grupo, meds]) => {
        const modCount = meds.filter(m => m.tieneOverride).length;
        return (
          <div key={grupo} className="border border-gray-100 rounded-xl overflow-hidden">
            <button type="button" onClick={() => toggleGrupo(grupo)}
              className="flex items-center justify-between w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors">
              <div className="flex items-center gap-2">
                {expandedGrupos.has(grupo) ? <ChevronDown size={15} className="text-text-secondary" /> : <ChevronRight size={15} className="text-text-secondary" />}
                <span className="font-semibold text-sm text-text-primary">{grupo}</span>
                <span className="text-[11px] text-text-secondary bg-white border border-gray-200 px-2 py-0.5 rounded-full">{meds.length}</span>
                {modCount > 0 && (
                  <span className="text-[10px] bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">{modCount} mod.</span>
                )}
              </div>
            </button>
            {expandedGrupos.has(grupo) && (
              <div className="divide-y divide-gray-50 px-3 pb-3 pt-2 space-y-1.5">
                {meds.map(m => (
                  <MedicamentoCard key={m.id} med={m} canEdit={canEdit} onEdit={() => setEditingMed(m)} />
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* Modal editor */}
      {editingMed && (
        <EditarMedModal med={editingMed} onClose={() => setEditingMed(null)} />
      )}

      {/* Modal crear */}
      {creando && (
        <CrearMedModal grupos={grupos} onClose={() => setCreando(false)} />
      )}
    </div>
  );
}
